import { NoticesComponentModule } from '@b2b/common/components/notices';
import { PermissionsDirectiveModule } from '@b2b/common/directives/permissions';
import { Notice, NoticeFactory } from '@b2b/common/factories/notice';
import { CartService, CartServiceModule, ModalService, ModalServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { ButtonGroupActionsService, ButtonGroupActionsServiceModule } from '@b2b/common/services/button-group-actions';
import { ContentHeaderService, ContentHeaderServiceModule } from '@b2b/common/services/contentHeader';
import {
  MembershipCardholderService,
  MembershipCardholderServiceModule,
} from '@b2b/common/services/membership/cardholder';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, ColumnDef, ContentHeaderProps, TODO } from '@b2b/types/core';
import {
  extractEmbeddedAddressFromCardholder,
  isPrimaryCardholder,
  isSecondaryCardholder,
  MembershipCardholder,
  MembershipDetails,
} from '@b2b/types/membership';
import { SubscriptionDetail } from '@b2b/types/subscriptions';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IScope, ITimeoutService } from 'angular';
import _ from 'lodash';
import moment from 'moment-timezone';
import invariant from 'tiny-invariant';
import { MembershipDetailService, MembershipDetailServiceModule } from '../services/membership-detail.service';
import { MembershipDetailsVM } from './types';

MembershipDetailController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  '$filter',
  'ApiService',
  'HelpersService',
  'CalendarService',
  'ThemesHelperService',
  'SubscriptionData',
  'MembershipData',
  'UserPreferencesService',
  'CartService',
  'MembershipDetailService',
  'MembershipCardholderService',
  'PermissionsService',
  'FeatureToggleService',
  'MembershipService',
  'FeatureService',
  'NoticeFactory',
  'ModalService',
  'ContentHeaderService',
  'GridDefinitionFactory',
  'ButtonGroupActionsService',
];

function MembershipDetailController(
  $scope: IScope & { membership: TODO; membershipDetail: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  apiService: ApiService,
  HelpersService: TODO,
  CalendarService: TODO,
  ThemesHelperService: ThemesHelperService,
  SubscriptionData: { data: ACMEResponse<SubscriptionDetail> },
  MembershipData: { data: ACMEResponse<MembershipDetails> },
  UserPreferencesService: TODO,
  CartService: CartService,
  membershipDetailService: MembershipDetailService,
  membershipCardholderService: MembershipCardholderService,
  PermissionsService: PermissionsService,
  FeatureToggleService: TODO,
  MembershipService: TODO,
  FeatureService: TODO,
  NoticeFactory: NoticeFactory,
  modalService: ModalService,
  contentHeaderService: ContentHeaderService,
  GridDefinitionFactory: TODO,
  buttonGroupActionsService: ButtonGroupActionsService
) {
  const ctrl = {
    model: {
      cardholders: [],
      cardholderAttributes: [],
      membershipJoinedDate: undefined,
      membershipStartDate: undefined,
      membershipExpirationDate: undefined,
      notes: '',
    } as Partial<MembershipDetailsVM>,
    vm: {
      cardholders: [],
    } as Pick<MembershipDetailsVM, 'cardholders'>,
    membershipCategory: null,
    searchQuery: '',
    isLoaded: false,
    isSaving: false,
    dateFormat: UserPreferencesService.getDateFormat('standardDate', 'dateFormat'),
    formatDate: (value: TODO) => {
      return $filter('date')(CalendarService.parseDate(value), ctrl.dateFormat);
    },
    searchName: '',
    membershipJoinedDate: '' as Date | string,
    membershipStartDate: '' as Date | string,
    membershipExpirationDate: '' as Date | string,
    registerChange: true,
    minDate: false,
    tmpCardholders: [] as MembershipCardholder[],
    subscriptionDetailsData: null as SubscriptionDetail | null,
    maxNumberOfCardHolders: 0,
    notice: new NoticeFactory() as Notice,
    cardholdersActions: buttonGroupActionsService.normalize({
      label: 'Add Cardholder',
      action: () => ctrl.addCardholder(),
      items: [
        {
          label: 'Send Digital Card(s)',
          permissions: ['MEMBERSHIP_CARD_SEND'],
          action: () => ctrl.sendDigitalCardEmail(),
        },
      ],
    }),

    dateTypeSelected: '',

    dateTypeName: {
      membershipJoinedDate: 'Membership Joined Date',
      membershipStartDate: 'Membership Start Date',
      membershipExpirationDate: 'Membership Expiration Date',
    },

    memberDetails: {
      columns: 1,
      columnWidth: 4,
      data: 'membershipDetail.model',
      include: [
        'isGift',
        'household',
        'householdId',
        'membershipStanding',
        'isLatestString',
        'membershipJoinedDate',
        'membershipStartDate',
        'membershipExpirationDate',
      ],
      mappings: {
        isLatestString: 'Is Latest',
      },
      filters: {
        membershipStanding: (value) => HelpersService.cap(value),
        membershipExpirationDate: (value) => ctrl.formatDate(value),
        membershipStartDate: (value) => ctrl.formatDate(value),
        membershipJoinedDate: (value) => ctrl.formatDate(value),
        isGift: (value) => (value ? 'Yes' : 'No'),
      },
      templates: {
        membershipJoinedDate: {
          permissions: 'MEMBERSHIP_UPDATE',
          template:
            '<div data-ng-controller="MembershipDetailDatePickerController" class="inputs input-group membership-detail-edit-field">\n' +
            '  <input id="date-picker-membership-detail" type="text"\n' +
            '         name="date-picker-membership-detail"\n' +
            '         class="form-control"\n' +
            '         data-ng-show="membershipDetailDatePicker.isPreferenceLoaded"\n' +
            '         data-uib-datepicker-popup="{{ membershipDetailDatePicker.getDateFormat() }}"\n' +
            '         data-datepicker-append-to-body="true"\n' +
            '         data-datepicker-options="membershipDetailDatePicker.dateOptions"\n' +
            '         data-datepicker-popup-template-url="common/includes/datePickerPopup.tpl.html"\n' +
            '         data-ng-model="membershipDetail.membershipJoinedDate"\n' +
            '         data-ng-required="required"\n' +
            '         data-ng-click="membershipDetailDatePicker.open($event)"\n' +
            '         data-ng-disabled="membershipDetail.isCanceled()"\n' +
            '         data-show-button-bar="false"\n' +
            '         data-min-date="membershipDetail.minDate"\n' +
            '         data-is-open="membershipDetailDatePicker.isOpen"\n' +
            '         data-close-text="Close" />\n' +
            '  <span class="date-btn input-group-btn">\n' +
            '    <button type="button" class="btn btn-default" data-ng-disabled="membershipDetail.isCanceled()" data-ng-click="membershipDetailDatePicker.open($event)" aria-label="To calendar"><i\n' +
            '      class="glyphicon glyphicon-calendar"></i></button>\n' +
            '  </span>\n' +
            '</div>\n' +
            '<em class="font-small note" data-ng-if="membershipDetail.isCanceled()"><span class="fa fa-warning" role="presentation"></span> Rejoin to update joined date</em>',
          getScope: $scope,
        },
        membershipStartDate: {
          permissions: 'MEMBERSHIP_UPDATE',
          template:
            '<div data-ng-controller="MembershipDetailDatePickerController" class="inputs input-group membership-detail-edit-field">\n' +
            '  <input id="date-picker-membership-detail" type="text"\n' +
            '         name="date-picker-membership-detail"\n' +
            '         class="form-control"\n' +
            '         data-ng-show="membershipDetailDatePicker.isPreferenceLoaded"\n' +
            '         data-uib-datepicker-popup="{{ membershipDetailDatePicker.getDateFormat() }}"\n' +
            '         data-datepicker-append-to-body="true"\n' +
            '         data-datepicker-options="membershipDetailDatePicker.dateOptions"\n' +
            '         data-datepicker-popup-template-url="common/includes/datePickerPopup.tpl.html"\n' +
            '         data-ng-model="membershipDetail.membershipStartDate"\n' +
            '         data-ng-required="required"\n' +
            '         data-ng-click="membershipDetailDatePicker.open($event)"\n' +
            '         data-ng-disabled="membershipDetail.isCanceled()"\n' +
            '         data-show-button-bar="false"\n' +
            '         data-min-date="membershipDetail.minDate"\n' +
            '         data-is-open="membershipDetailDatePicker.isOpen"\n' +
            '         data-close-text="Close" />\n' +
            '  <span class="date-btn input-group-btn">\n' +
            '    <button type="button" class="btn btn-default" data-ng-disabled="membershipDetail.isCanceled()" data-ng-click="membershipDetailDatePicker.open($event)" aria-label="To calendar"><i\n' +
            '      class="glyphicon glyphicon-calendar"></i></button>\n' +
            '  </span>\n' +
            '</div>\n' +
            '<em class="font-small note" data-ng-if="membershipDetail.isCanceled()"><span class="fa fa-warning" role="presentation"></span> Rejoin to update start date</em>',
          getScope: $scope,
        },
        membershipExpirationDate: {
          permissions: 'MEMBERSHIP_UPDATE',
          template:
            '<div data-ng-controller="MembershipDetailDatePickerController" class="inputs input-group membership-detail-edit-field">\n' +
            '  <input id="date-picker-membership-detail" type="text"\n' +
            '         name="date-picker-membership-detail"\n' +
            '         class="form-control"\n' +
            '         data-ng-show="membershipDetailDatePicker.isPreferenceLoaded"\n' +
            '         data-uib-datepicker-popup="{{ membershipDetailDatePicker.getDateFormat() }}"\n' +
            '         data-datepicker-append-to-body="true"\n' +
            '         data-datepicker-options="membershipDetailDatePicker.dateOptions"\n' +
            '         data-datepicker-popup-template-url="common/includes/datePickerPopup.tpl.html"\n' +
            '         data-ng-model="membershipDetail.membershipExpirationDate"\n' +
            '         data-ng-required="required"\n' +
            '         data-ng-click="membershipDetailDatePicker.open($event)"\n' +
            '         data-ng-disabled="membershipDetail.isCanceled()"\n' +
            '         data-show-button-bar="false"\n' +
            '         data-min-date="membershipDetail.minDate"\n' +
            '         data-is-open="membershipDetailDatePicker.isOpen"\n' +
            '         data-close-text="Close" />\n' +
            '  <span class="date-btn input-group-btn">\n' +
            '    <button type="button" class="btn btn-default" data-ng-disabled="membershipDetail.isCanceled()" data-ng-click="membershipDetailDatePicker.open($event)" aria-label="To calendar"><i\n' +
            '      class="glyphicon glyphicon-calendar"></i></button>\n' +
            '  </span>\n' +
            '</div>\n' +
            '<em class="font-small note" data-ng-if="membershipDetail.isCanceled()"><span class="fa fa-warning" role="presentation"></span> Rejoin to update expiration date</em>',
          getScope: $scope,
        },
      },
    },

    memberCategoryDetails: {
      columns: 3,
      data: 'membershipDetail.model',
      layout: 'Z',
      include: [
        'membershipCategory',
        'offeringName',
        'shortDescription',
        'auxiliaryMembership',
        'membershipLevelCollectionName',
      ],
      mappings: {
        membershipCategory: 'Membership Level',
        offeringName: 'Membership Offering',
        shortDescription: 'Short Description',
        auxiliaryMembership: 'Is Auxiliary',
        membershipLevelCollectionName: 'Auxiliary Group',
      },
      filters: {
        auxiliaryMembership: (value) => (value ? 'Yes' : 'No'),
      },
    },

    allowUpgradeExternalMemberships: false,

    subscriptionDetails: {
      columns: 3,
      data: 'membershipDetail.model',
      layout: 'Z',
      include: [
        'subscriptionId',
        'subscriptionStatus',
        'subscriptionStatusChangeDate',
        'nextPaymentDate',
        'nextPayment',
        'cardOnFile',
      ],
      mappings: {
        subscriptionId: 'Subscription ID',
        subscriptionStatus: 'Subscription Status',
        subscriptionStatusChangeDate: 'Subscription Status Change Date',
        nextPaymentDate: 'Next Payment Date',
        membershipLevelCollectionName: 'Next Payment',
        cardOnFile: 'Card on File',
      },
      filters: {
        subscriptionStatus: (value) => value.charAt(0).toUpperCase() + value.substring(1),
        nextPaymentDate: (value) => ctrl.formatDate(value),
        subscriptionStatusChangeDate: (value) => ctrl.formatDate(value),
      },
    },

    idDetails: {
      columns: 2,
      columnWidth: 4,
      data: 'membershipDetail.model',
      include: ['acmeMembershipNumber', 'externalMembershipId', 'importId', 'orderIdLink'],
      mappings: {
        orderIdLink: 'Order Number',
      },
    },

    contactDetails: {
      columns: 3,
      columnWidth: 4,
      showEmpty: true,
      data: 'membershipDetail.model.primaryCardholder',
      include: [
        'name',
        'orgName',
        'email',
        'phoneNumber',
        'streetAddress1',
        'streetAddress2',
        'city',
        'state',
        'country',
        'zipCode',
        'cardType',
        'acmeCustomerNumber',
        'acmeBarcode',
        'constituentId',
        'barcode',
        'constituentImportId',
      ],
      mappings: {
        constituentId: 'External Customer Id',
        barcode: 'External Barcode',
        streetAddress1: 'Street Address 1',
        streetAddress2: 'Street Address 2',
      },
    },

    salesForceMembershipDetails: {
      columns: 1,
      columnWidth: 4,
      data: 'membershipDetail.model.details',
      sorting: false,
      include: [
        'admits',
        'filmPlusStatus',
        'filmPlusExpirationDate',
        'ps1PlusStatus',
        'ps1ExpirationDate',
        'juniorAssociateStatus',
        'juniorAssociateCategory',
        'juniorAssociateExpirationDate',
      ],
      mappings: {
        admits: 'Admits',
        filmPlusStatus: 'Film Plus Status',
        filmPlusExpirationDate: 'Film Plus Expiration Date',
        ps1PlusStatus: 'PS1 Plus Status',
        ps1ExpirationDate: 'PS1 Plus Expiration Date',
        juniorAssociateStatus: 'Junior Associate Status',
        juniorAssociateCategory: 'Junior Associate Category',
        juniorAssociateExpirationDate: 'Junior Associate Expiration Date',
      },
    },

    raisersEdgeMembershipDetails: {
      columns: 3,
      columnWidth: 4,
      data: 'membershipDetail.model',
      sorting: false,
      include: ['program', 'category', 'subcategory'],
    },

    viewId: '',
    cardholdersGridDefinition: new GridDefinitionFactory(),
    cardholdersGridName: $state.current.name + '.grid-cardholders',
    cardholdersGridOptions: {
      data: 'membershipDetail.vm.cardholders',
      useAccessibleGrid: true,
      showFooter: true,
      multiSelect: false,
      enableRowSelection: false,
      enableSorting: false,
      useExternalPagination: false,
      useExternalSorting: false,
      paginate: true,
      pageSize: 50,
      appScopeProvider: {
        membership: null as Partial<MembershipDetailsVM> | null,
        selectedPrimaryCardholderId: null as number | null,

        formatDate: (value) => ctrl.formatDate(value),

        onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
          ctrl.refreshCardholdersGrid();
        },

        onDblClick: (row: TODO) => {
          ctrl.gridDoubleClick(row);
        },

        onPrimaryCardholderClick: (event: PointerEvent, cardholder: MembershipCardholder) => {
          if (ctrl.cardholdersGridOptions.appScopeProvider.selectedPrimaryCardholderId === cardholder.id) {
            event.preventDefault();
            return;
          }

          const isAutorenewMembership = membershipDetailService.isActiveSubscription(ctrl.subscriptionDetailsData);
          const result = membershipCardholderService.checkAllowedToSwapPrimaryCardholder({ isAutorenewMembership });

          if (!result.ok) {
            const messages = result.errors.map((message) => ({ message }));
            ctrl.notice.showErrorMessages(messages, { duration: 10_000 });

            event.preventDefault();
            return;
          }

          if (membershipCardholderService.validateForPrimary(cardholder, { isAutorenewMembership })) {
            return;
          }

          event.preventDefault();

          const message = `<strong>Cannot Change to Primary</strong>: This cardholder cannot become Primary until all required fields are populated (First Name, Last Name, Name on Card, Address). Please make the changes and try again.`;
          ctrl.notice.showErrorMessages([{ message }], { duration: 10_000 });
        },

        onPrimaryCardholderChange: (cardholder: MembershipCardholder) => {
          const payload = membershipDetailService.createSwappedPrimaryCardholderModel(ctrl.model, cardholder);
          const primaryCardholderId = ctrl.model.primaryCardholder?.id ?? null;

          return apiService.membership
            .update(payload)
            .then((response) => response.data.payload)
            .then(() => {
              membershipCardholderService.showPrimaryCardholderChangedMessage(ctrl.notice);
              ctrl.reload();
            })
            .catch((response) => {
              ctrl
                .showMessage({
                  title: 'An error occurred',
                  message: response.data.error,
                })
                .then(() => {
                  ctrl.setSelectedPrimaryCardholderId(primaryCardholderId);
                });
            });
        },
      },
      columnDefs: [
        {
          field: 'view',
          displayName: 'View',
          visible: true,
          enableHiding: false,
          enableSorting: false,
          width: 50,
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents"
                 ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.onDblClick(row)"
                 role="link">
                View<span class="sr-only"> Details for {{ row.entity.name }}</span>
              </a>
            </div>
          `,
        },
        FeatureService.isEnabled(FeatureService.customers) && PermissionsService.check('CUSTOMERS_VIEW')
          ? {
              field: 'customerId',
              displayName: 'Customer Number',
              visible: true,
              enableSorting: false,
              cellTemplate: `
                <div class="ui-grid-cell-contents">
                  <a data-ui-sref="application.customers.individuals.detail({ id: row.entity.customerId })">
                    {{ row.entity.customerId }}
                  </a>
                </div>
              `,
            }
          : {
              field: 'customerId',
              displayName: 'Customer Number',
              visible: true,
              enableSorting: false,
            },
        {
          field: 'name',
          displayName: 'Name on Card',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'barcode',
          displayName: 'External Barcode',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'firstName',
          displayName: 'First Name',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'lastName',
          displayName: 'Last Name',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'cardType',
          displayName: 'Card Type',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'email',
          displayName: 'Email',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'cardStatus',
          displayName: 'Card Status',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'acmeBarcode',
          displayName: 'ACME Barcode',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'acmeCustomerNumber',
          displayName: 'ACME Customer Number',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'cardImportId',
          displayName: 'Card Import ID',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'id',
          displayName: 'ID',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'expirationDate',
          displayName: 'Expiration Date',
          visible: false,
          enableSorting: false,
          cellTemplate: `<div class="ui-grid-cell-contents">{{ grid.appScope.formatDate(row.entity.expirationDate) }}</div>`,
        },
        {
          field: 'startDate',
          displayName: 'Start Date',
          visible: false,
          enableSorting: false,
          cellTemplate: `<div class="ui-grid-cell-contents">{{ grid.appScope.formatDate(row.entity.startDate) }}</div>`,
        },
        {
          field: 'phoneNumber',
          displayName: 'Phone Number',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'streetAddress1',
          displayName: 'Address 1',
          visible: true,
          enableSorting: false,
        },
        {
          field: 'streetAddress2',
          displayName: 'Address 2',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'city',
          displayName: 'City',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'state',
          displayName: 'State',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'country',
          displayName: 'Country',
          visible: false,
          enableSorting: false,
        },
        {
          field: 'zipCode',
          displayName: 'Zip',
          visible: true,
          enableSorting: false,
        },
        ...(PermissionsService.every(['MEMBERSHIP_UPDATE', 'MEMBERSHIP_CARD_UPDATE'])
          ? [
              {
                field: 'setAsPrimary',
                displayName: 'Primary',
                visible: true,
                enableSorting: false,
                enableHiding: false,
                cellTemplate: `
                  <div class="ui-grid-cell-contents">
                    <input type="radio"
                           name="setAsPrimary"
                           class="grid-input"
                           ng-disabled="grid.appScope.membership && grid.appScope.membership.membershipStanding !== 'active'"
                           ng-value="row.entity.id"
                           ng-model="grid.appScope.selectedPrimaryCardholderId"
                           ng-click="grid.appScope.onPrimaryCardholderClick($event, row.entity)"
                           ng-change="grid.appScope.onPrimaryCardholderChange(row.entity)" />
                  </div>
                `,
              },
            ]
          : []),
      ] as ColumnDef[],
    },

    contentTitle: {
      title: 'Membership Details',
      buttonLeft: {
        label: '< Back',
        action: () => {
          window.history.go(-1);
        },
      },
      buttonRight: undefined,
    } as ContentHeaderProps,

    eligibility: false,

    init: function () {
      ctrl.viewId = $state.current.name ?? '';
      contentHeaderService.update(ctrl.contentTitle);

      ctrl.subscriptionDetailsData = SubscriptionData.data?.success ? SubscriptionData.data.payload : null;
      ctrl.maxNumberOfCardHolders = ctrl.subscriptionDetailsData?.membershipOffering.rules.numberOfCardHolders ?? 0;

      if (MembershipData.data && MembershipData.data.success) {
        ctrl.success.init(MembershipData.data.payload);
      } else {
        ctrl.error.init(MembershipData);
      }

      ctrl.initConfigData();

      $scope.$on('$destroy', $scope.$watch('membershipDetail.membershipJoinedDate', ctrl.onMembershipJoinedDateUpdate));
      $scope.$on('$destroy', $scope.$watch('membershipDetail.membershipStartDate', ctrl.onMembershipStartDateUpdate));
      $scope.$on(
        '$destroy',
        $scope.$watch('membershipDetail.membershipExpirationDate', ctrl.onMembershipExpirationDateUpdate)
      );

      ctrl.cardholdersGridDefinition.init(
        ctrl.cardholdersGridName,
        ctrl.cardholdersGridOptions,
        ctrl.viewId,
        $state.current.data.enablePreferences
      );

      $scope.$on('DataGrid::UserPreferencesLoaded', () => {
        ctrl.refreshCardholdersGrid();
      });
    },

    setCardholders(cardholders: MembershipCardholder[]) {
      ctrl.model.cardholders = cardholders;

      const list = _.orderBy(angular.copy(cardholders), ['name'], ['asc']);
      const primary = list.filter(isPrimaryCardholder);
      const secondaries = _.sortBy(list.filter(isSecondaryCardholder), 'name');
      ctrl.vm.cardholders = [...primary, ...secondaries];
    },

    refreshCardholdersGrid() {
      const totalItems = ctrl.vm.cardholders ? ctrl.vm.cardholders.length : 0;
      ctrl.cardholdersGridDefinition.isLoaded = true;

      $timeout(() => {
        $scope.$broadcast('DataGrid::ResizeGrid', {
          name: ctrl.cardholdersGridDefinition.options.gridName,
          datasetLength: totalItems,
          totalItems: totalItems,
        });
      }, 10);
    },

    initConfigData: function () {
      ctrl.allowUpgradeExternalMemberships = ThemesHelperService.getCompany('allowUpgradeExternalMemberships') as TODO;
    },

    processCategory: function (category) {
      let offering;

      if (ctrl.model.offeringId) {
        for (let i = 0, ii = category.offerings.length; i < ii; i++) {
          if (category.offerings[i].id === ctrl.model.offeringId) {
            offering = category.offerings[i];
            break;
          }
        }
      }

      if (offering) {
        if (ctrl.model.pricePointId) {
          if (
            Array.isArray(offering.priceLists) &&
            offering.priceLists.length &&
            Array.isArray(offering.priceLists[0].prices) &&
            offering.priceLists[0].prices.length
          ) {
            for (let i = 0, ii = offering.priceLists[0].prices.length; i < ii; i++) {
              if (offering.priceLists[0].prices[i].personType.id === ctrl.model.pricePointId) {
                category.membershipPricePointName = offering.priceLists[0].prices[i].personType.name;
                break;
              }
            }
          }
        }

        if (!ThemesHelperService.getCompany('auxiliaryMembership') && !category.auxiliary) {
          category.auxiliary = null;
          delete category.auxiliary;

          category.auxiliaryGroup = null;
          delete category.auxiliaryGroup;
        } else {
          if (
            Array.isArray(category.membershipLevelCollection) &&
            category.membershipLevelCollection[0] &&
            category.membershipLevelCollection[0].name
          ) {
            category.membershipLevelCollectionName = category.membershipLevelCollection[0].name;
          }
        }

        category.membershipOfferingName = offering.name;
      }

      return category;
    },

    showMessage: function (props: {
      title: string;
      message: string;
      okText?: string;
      hideOkText?: boolean;
      cancelText?: string;
    }) {
      if (!props.message) {
        return Promise.resolve(false);
      }

      return modalService.confirm(props);
    },

    isCanceled: function () {
      return ctrl.model.membershipStanding?.toLowerCase() === 'dropped';
    },

    /**
     * Checks to see whether there are any
     * conflicts when changing the expiration date.
     *
     * Results in an modal only if there is an error.
     *
     * @param response
     */
    checkExpirationConflicts: function (response) {
      apiService.membership
        .checkExpirationConflicts(ctrl.model.membershipExpirationDate)
        .then(this.success.checkExpirationConflicts, this.error.checkExpirationConflicts);
    },

    /**
     * Checks whether card holders match new expiration date.
     *
     * Results in a modal if there is a response with cardholders that require updates.
     *
     * @param membershipDateType membershipJoinedDate | membershipStartDate | membershipExpirationDate
     */
    updateMembershipDate: function (membershipDateType: string) {
      const dateType = membershipDateType;

      const reasignOldValue = () => {
        // Assign old value from model instead of controller
        ctrl.registerChange = false;
        ctrl.membershipJoinedDate = CalendarService.parseDate(ctrl.model.membershipJoinedDate);
        ctrl.membershipStartDate = CalendarService.parseDate(ctrl.model.membershipStartDate);
        ctrl.membershipExpirationDate = CalendarService.parseDate(ctrl.model.membershipExpirationDate);
        // Restore revert on next tick
        $timeout(() => {
          ctrl.registerChange = true;
        }, 0);
      };

      ctrl.dateTypeSelected = membershipDateType;

      if (ctrl.registerChange) {
        ctrl.tmpCardholders = angular.copy(ctrl.model.cardholders ?? []);
        $scope.$broadcast('MembershipDetailDatePickerController::Close');

        const modal = modalService.open({
          templateUrl:
            'views/application/views/membership/detail/modals/cardholderExpiration/cardholderExpiration.tpl.html',
          size: 'lg',
          controller: 'MembershipDetailModalsCardholderExpirationController',
          resolve: {
            membershipJoinedDate: () => {
              return ctrl.membershipJoinedDate;
            },
            membershipStartDate: () => {
              return ctrl.membershipStartDate;
            },
            membershipExpirationDate: () => {
              return ctrl.membershipExpirationDate;
            },
            membershipDateType: () => {
              return dateType;
            },
            membership: () => {
              return ctrl.model;
            },
          },
        });
        // Invoked when modal completes with close(params)
        modal.result.then(() => {
          const data = angular.copy(ctrl.model);

          const toMoment = (d: Date) => {
            return moment
              .tz([d.getFullYear(), d.getMonth(), d.getDate()], ThemesHelperService.getCompany('timezone'))
              .format('ZZ');
          };

          const toDate = (d: Date, tz: string, ...hour: number[]) => {
            return CalendarService.toTZISO8601(new Date(d.getFullYear(), d.getMonth(), d.getDate(), ...hour), tz);
          };

          // membershipJoinedDate change on membership
          let d = ctrl.membershipJoinedDate as Date;
          let targetTz = toMoment(d);
          data.membershipJoinedDate = toDate(d, targetTz, 0, 0, 0, 0);

          // membershipStartDate change on membership
          d = ctrl.membershipStartDate as Date;
          targetTz = toMoment(d);
          data.membershipStartDate = toDate(d, targetTz, 0, 0, 0, 0);

          // membershipStartDate on cardholders
          data.cardholders = data.cardholders?.map((cardholder) => {
            cardholder.startDate = data.membershipStartDate;
            return cardholder;
          });

          // membershipExpirationDate change on membership
          d = ctrl.membershipExpirationDate as Date;
          targetTz = toMoment(d);
          data.membershipExpirationDate = toDate(d, targetTz, 23, 59, 59, 0);

          apiService.membership.update(data).then(ctrl.success.updateExpiration, ctrl.error.updateExpiration);
        }, reasignOldValue);
      }
    },

    gridDoubleClick: function (item: TODO) {
      if (!item.entity.cardImportId) {
        $scope.membership.showMessage({
          title: 'Unable to view cardholder',
          message: 'This cardholder does not contain a record in ACME.',
        });

        return;
      }

      $state.go('application.membership.detail.member', { memberId: item.entity.cardImportId }, { reload: true });
    },

    getCustomer: function () {
      let result: { customerId: any; cardholder?: any } = {
        customerId: null,
      };

      if (ctrl.model.primaryCardholder && ctrl.model.primaryCardholder.customerId) {
        result = {
          customerId: ctrl.model.primaryCardholder.customerId,
          cardholder: ctrl.model.primaryCardholder,
        };
      }

      if (!result) {
        ctrl.model.cardholders?.forEach((cardholder) => {
          if (!result && cardholder.customerId) {
            result = {
              customerId: cardholder.customerId,
              cardholder: cardholder,
            };
          }
        });
      }

      return result;
    },

    transactAsMember: function () {
      /**
       * TODO: The membership used to purchase this order item could be different from the membershipId of the item itself.
       *       Especially when auxiliary memberships come into play.
       */

      ctrl.updateOrderContact(() => {
        ctrl.goToState('application.programs.list');
      });
    },

    cancelMembership: function () {
      apiService.membership.eligibility
        .listByMembershipCategory({
          membershipId: String(ctrl.model.id),
          saleChannels: 'insideSalesIndividual',
          actions: CartService.memberships.ENUMS['cancel'].join(),
          summarize: 0,
        })
        .then((response) => {
          const canCancel = response.data.payload.canCancel;

          if (!canCancel) {
            return Promise.reject();
          }

          const organizationOrderInput = {
            memberships: [
              {
                itemType: 'MembershipCancel',
                membershipInfo: {
                  membershipId: ctrl.model.id,
                  membershipOfferingId: ctrl.model.offeringId,
                  waiveBenefits: false,
                  pricePointId: ctrl.model.pricePointId,
                  notifyGiftRecipient: false,
                  isGift: ctrl.model.isGift,
                  membershipCategoryId: ctrl.model.categoryId,
                  membershipCards: ctrl.model.cardholders,
                },
              },
            ],
          };

          apiService.orders.checkout.charges(organizationOrderInput).then((response) => {
            ctrl.goToState('application.membership.cancel');
          });
        })
        .catch(() => {
          ctrl.showMessage({
            title: 'Membership is not eligible',
            message: ['The membership for', ctrl.model.primaryCardholder?.name, 'is not eligible to cancel'].join(' '),
          });
        });
    },

    checkLifeCycle: function (type) {
      const isQuickRenew = !type;
      const lifecycleType = type || 'renew';
      /**
       * TODO: The membership used to purchase this order item could be different from the membershipId of the item itself.
       *       Especially when auxiliary memberships come into play.
       */
      const membership: {
        quantity: any;
        itemType: any;
        membershipId: any;
        orderContactId: any;
        categoryId: any;
        membershipCategory: any;
        offeringId: any;
        offeringName: any;
        pricePointId: any;
        amount?: any;
      } = {
        quantity: 1,
        itemType: MembershipService.getPurchaseType('renew'),
        membershipId: ctrl.model.id,
        orderContactId: ctrl.getCustomer().customerId,
        categoryId: ctrl.model.categoryId,
        membershipCategory: ctrl.model.membershipCategory,
        offeringId: ctrl.model.offeringId,
        offeringName: ctrl.model.offeringName,
        pricePointId: ctrl.model.pricePointId,
      };

      invariant(ctrl.model.id, 'membershipId is required');

      CartService.memberships
        .lifecycle(CartService.memberships.ENUMS[lifecycleType], {
          membershipId: ctrl.model.id,
          membershipCategory: ctrl.model.membershipCategory,
          membershipName: ctrl.model.primaryCardholder?.name,
        })
        .then((response) => {
          const payload = response.data.payload;
          const allowUpgrade =
            !payload.hasOwnProperty('externalMembership') ||
            !payload.externalMembership ||
            (ctrl.allowUpgradeExternalMemberships && payload.externalMembership);
          const found = CartService.memberships.hasPricepointMatch(membership, payload);

          if (isQuickRenew && found) {
            membership.amount = found.pricepoint.price;
            membership.itemType = MembershipService.getPurchaseType(found.type || 'renew');
            ctrl.updateOrderContact(() => {
              CartService.memberships.addToCart(membership).then(ctrl.success.updateCart, ctrl.error.updateCart);
            });
          } else {
            ctrl.updateOrderContact(
              () => {
                ctrl.goToState('application.membership.retail', lifecycleType);
              },
              lifecycleType,
              allowUpgrade
            );
          }
        }, ctrl.error.renewMembership);
    },

    updateOrderContact: function (callBack: () => void, lifecycleType?: string, allowUpgrade?: boolean) {
      const hasNewMembershipPurchase = !!CartService.memberships.hasNewMembership();
      const isUpgradeDowngrade = lifecycleType && lifecycleType === 'upgradeDowngrade';
      const hasMembershipsInCart = CartService.memberships.hasMemberships();
      const hasItemsInCart = !!CartService.getItems().length;
      const customer = ctrl.getCustomer();
      const isOrderContact = CartService.isOrderContact(customer.customerId);

      const showPrompt =
        (CartService.hasOrderContact() && !isOrderContact) ||
        hasNewMembershipPurchase ||
        (isOrderContact && isUpgradeDowngrade && (hasMembershipsInCart || hasItemsInCart)) ||
        (isUpgradeDowngrade && !allowUpgrade);

      let title = 'Are you sure?';
      let message: string | null = null;
      let cancel = 'Cancel';
      let button = 'Continue';
      let hideButton = false;

      if (showPrompt) {
        if (hasNewMembershipPurchase) {
          message = 'Continuing will remove any memberships in the cart.';
        } else if (!customer.customerId) {
          message =
            'Do you want to change the transacting member from ' +
            HelpersService.getFullName(CartService.getOrderContact()) +
            '?' +
            '<br><br>Doing so will remove any memberships in the cart.';
        } else if (isUpgradeDowngrade && !allowUpgrade) {
          title = 'Membership not eligible';
          message =
            'This membership was created outside of ACME. You cannot Upgrade / Downgrade within ACME, due to your current configuration.';
          cancel = 'Close';
          hideButton = true;
        } else if (isOrderContact && isUpgradeDowngrade && hasMembershipsInCart) {
          message =
            'The cart already contains a membership for ' +
            HelpersService.getFullName(CartService.getOrderContact()) +
            '.' +
            ' <br><br>Adding this new membership will clear the current membership from the cart.';
          button = 'Update Cart';
        } else if (isOrderContact && isUpgradeDowngrade && hasItemsInCart) {
          message =
            'Please add membership and tickets separately. Once you Upgrade / Downgrade, tickets will reflect applicable benefits.';
          cancel = 'Buy Tickets First';
          button = 'Upgrade / Downgrade Membership First';
        } else {
          message =
            'Do you want to transact as ' +
            HelpersService.getFullName(customer.cardholder) +
            ' instead of ' +
            HelpersService.getFullName(CartService.getOrderContact()) +
            '? ' +
            '<br><br>Doing so will remove any memberships in the cart.';
        }

        ctrl
          .showMessage({
            title: title,
            message: message,
            okText: button,
            cancelText: cancel,
            hideOkText: hideButton,
          })
          .then((confirmed) => {
            if (confirmed) {
              CartService.cancelCart().then(() => {
                ctrl.updateContactAndProceed(callBack);
              });
            }
          });
      } else {
        ctrl.updateContactAndProceed(callBack);
      }
    },

    updateContactAndProceed: function (callBack) {
      CartService.memberships
        .updateOrderContact({
          membershipId: ctrl.model.id,
          cardholders: ctrl.model.cardholders,
          orderContactId: ctrl.getCustomer().customerId,
        })
        .then(callBack, ctrl.error.renewMembership);
    },

    goToState: function (state, lifecycleType?: string) {
      const props: {
        membershipId: any;
        orderContactId?: any;
        lifecycleType?: any;
      } = { membershipId: ctrl.model.id };
      const customer = ctrl.getCustomer();

      if (customer.customerId) {
        props.orderContactId = customer.customerId;
      }

      if (lifecycleType) {
        props.lifecycleType = lifecycleType;
      }

      $state.go(state, props);
    },

    updateNotes: () => {
      if (typeof ctrl.model.notes !== 'string') {
        ctrl.model.notes = '';
      }

      ctrl.isSaving = true;

      apiService.membership
        .notes({ id: $state.params.id, notes: ctrl.model.notes })
        .then(() => {
          ctrl.isSaving = false;
        })
        .finally(() => {
          ctrl.isSaving = false;
        });
    },

    reload() {
      invariant(ctrl.model.id);

      return apiService.membership
        .detail(ctrl.model.id)
        .then((response) => response.data.payload)
        .then((response) => {
          ctrl.success.init(response);
        });
    },

    canAddCardholders: () => {
      switch (ctrl.model.membershipStanding) {
        case 'active':
          return true;

        default:
          return false;
      }
    },

    hasReachedCardholderLimit: () => {
      const cardholders = ctrl.model.cardholders ?? [];

      return cardholders.length >= ctrl.maxNumberOfCardHolders;
    },

    shouldDisableAddCardholder: () => {
      return !ctrl.canAddCardholders() || ctrl.hasReachedCardholderLimit();
    },

    addCardholder: () => {
      const isAutorenewMembership = membershipDetailService.isActiveSubscription(ctrl.subscriptionDetailsData);

      membershipCardholderService
        .showCardholderModal({
          mode: 'create',
          cardholder: null,
          isAutorenewMembership,
          membershipAddresses: ctrl.model.id
            ? { id: ctrl.model.id, addresses: ctrl.model.cardholders?.map(extractEmbeddedAddressFromCardholder) ?? [] }
            : undefined,
        })
        .result.then((response) => {
          const cardholder = response.contact;

          switch (cardholder.cardType) {
            case 'primary': {
              return membershipDetailService.addNewPrimaryCardholder(ctrl.model, cardholder).then((response) => {
                membershipCardholderService.showPrimaryCardholderChangedMessage(ctrl.notice);
                return response;
              });
            }

            case 'secondary': {
              return membershipDetailService.addNewSecondaryCardholder(ctrl.model, cardholder);
            }
          }
        })
        .then(() => {
          return ctrl.reload();
        })
        .catch((response: string | { data: { error: string } }) => {
          if (typeof response === 'string') {
            return;
          }

          ctrl.showMessage({
            title: 'An error occurred',
            message: response.data.error,
          });
        });
    },

    sendDigitalCardEmail: () => {
      return modalService
        .confirm({
          title: 'Send Digital Card Email?',
          message: `Would you like to send the digital membership card email to the Primary member? Digital cards for the Primary Cardholder and any Secondary cardholders will be included.`,
          okText: 'Send',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          invariant(ctrl.model.id);

          return apiService.membership.sendDigitalCardEmail(ctrl.model.id).catch((response) => {
            ctrl.showMessage({
              title: 'An error occurred',
              message: response.data.error,
            });
          });
        });
    },

    setSelectedPrimaryCardholderId: (cardholderId: number | null) => {
      ctrl.cardholdersGridOptions.appScopeProvider.selectedPrimaryCardholderId = cardholderId;
    },

    success: {
      init: function (payload: MembershipDetails) {
        membershipDetailService.fetchLevelDetails(payload.categoryId).then((response) => {
          ctrl.maxNumberOfCardHolders = response.data.payload.rules.numberOfCardHolders;
        });

        $timeout(() => {
          ctrl.model = membershipDetailService.process(payload, ctrl.subscriptionDetailsData);
          ctrl.setCardholders(ctrl.model.cardholders ?? []);
          ctrl.cardholdersGridOptions.appScopeProvider.membership = ctrl.model;
          ctrl.setSelectedPrimaryCardholderId(ctrl.model.primaryCardholder?.id ?? null);
          ctrl.membershipJoinedDate = CalendarService.parseDate(ctrl.model.membershipJoinedDate);
          ctrl.membershipStartDate = CalendarService.parseDate(ctrl.model.membershipStartDate);
          ctrl.membershipExpirationDate = CalendarService.parseDate(ctrl.model.membershipExpirationDate);
          ctrl.minDate = CalendarService.parseDate(ctrl.model.membershipStartDate);
          ctrl.refreshCardholdersGrid();
          ctrl.isLoaded = true;

          membershipDetailService.fetchOrderDetails(payload.orderId).then((orderLink) => {
            ctrl.model.orderIdLink = orderLink;
          });

          if (
            FeatureToggleService.check('TENANT_CART') &&
            PermissionsService.check(['ORDER_CREATE', 'ACCT_ORDERS_CREATE']) &&
            ctrl.model.membershipStanding &&
            ctrl.model.membershipStanding !== 'new'
          ) {
            ctrl.contentTitle.buttonRight = {
              label: 'Transact',
              action: () => {
                ctrl.transactAsMember();
              },
              items: PermissionsService.check('MEMBERSHIP_SALES')
                ? [
                    {
                      label: 'Quick Renew',
                      action: () => {
                        ctrl.checkLifeCycle(null);
                      },
                    },
                    {
                      label: 'Renew Options',
                      action: () => {
                        ctrl.checkLifeCycle('renew');
                      },
                    },
                    {
                      label: 'Upgrade/Downgrade',
                      action: () => {
                        ctrl.checkLifeCycle('upgradeDowngrade');
                      },
                    },
                    {
                      label: 'Cancel',
                      action: () => {
                        ctrl.cancelMembership();
                      },
                    },
                  ]
                : undefined,
            };

            contentHeaderService.update(ctrl.contentTitle);
          }
        }, 0);
      },
      updateExpiration: function (response) {
        const payload = response.data.payload;
        ctrl.registerChange = false;
        ctrl.model.membershipExpirationDate = payload.membershipExpirationDate;
        ctrl.membershipJoinedDate = CalendarService.parseDate(payload.membershipJoinedDate);
        ctrl.membershipStartDate = CalendarService.parseDate(payload.membershipStartDate);
        ctrl.membershipExpirationDate = CalendarService.parseDate(payload.membershipExpirationDate);
        ctrl.model.membershipStanding = payload.membershipStanding;
        ctrl.setCardholders(payload.cardholders);

        ctrl.notice.showSuccessMessages(
          [{ message: `${ctrl.dateTypeName[ctrl.dateTypeSelected]} Successfully saved` }],
          { duration: 3000 }
        );

        $timeout(() => {
          ctrl.registerChange = true;
        }, 0);
      },
      updateCart: (response) => {
        $scope.$emit('Cart::Open');
      },
    },

    error: {
      init: (response) => {
        ctrl.isLoaded = true;
      },
      updateCart: (response) => {
        ctrl.showMessage({
          title: 'An error occurred',
          message: 'Unable to update cart',
        });
      },
      renewMembership: (response) => {
        ctrl.showMessage({
          title: 'An error occurred',
          message: response.data.error,
        });
      },
      updateExpiration: (response) => {
        ctrl.registerChange = false;
        ctrl.membershipJoinedDate = CalendarService.parseDate(ctrl.model.membershipJoinedDate);
        ctrl.membershipStartDate = CalendarService.parseDate(ctrl.model.membershipStartDate);
        ctrl.membershipExpirationDate = CalendarService.parseDate(ctrl.model.membershipExpirationDate);
        ctrl.setCardholders(angular.copy(ctrl.tmpCardholders));
        ctrl.tmpCardholders = [];
        ctrl
          .showMessage({
            title: 'Invalid Date Selected',
            message: response.data.error,
          })
          .then(() => {
            ctrl.registerChange = true;
          });
      },
    },

    checkDateEquality: function (firstDate: Date | string, secondDate: Date | string) {
      return moment(firstDate).isSame(secondDate);
    },

    onMembershipJoinedDateUpdate: function (newValue: Date | string, oldValue: Date | string) {
      if (newValue && oldValue && !ctrl.checkDateEquality(newValue, oldValue)) {
        ctrl.updateMembershipDate('membershipJoinedDate');
      }
    },

    onMembershipStartDateUpdate: function (newValue: Date | string, oldValue: Date | string) {
      if (newValue && oldValue && !ctrl.checkDateEquality(newValue, oldValue)) {
        ctrl.updateMembershipDate('membershipStartDate');
      }
    },

    onMembershipExpirationDateUpdate: function (newValue: Date | string, oldValue: Date | string) {
      if (newValue && oldValue && !ctrl.checkDateEquality(newValue, oldValue)) {
        ctrl.updateMembershipDate('membershipExpirationDate');
      }
    },
  };

  $scope.membershipDetail = ctrl;
  ctrl.init();
}

export const MembershipDetailControllerModule = angular
  .module('acme.view.application.membership.detail.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.themesHelper',
    'acme.common.service.calendar',
    'acme.common.service.userPreferences',
    CartServiceModule,
    MembershipDetailServiceModule,
    MembershipCardholderServiceModule,
    'acme.common.service.permissions',
    'acme.common.service.membership',
    'acme.common.service.featureToggle',
    'acme.common.service.feature',
    ModalServiceModule,
    ContentHeaderServiceModule,
    'acme.view.application.modals.confirm.controllers',
    'acme.view.application.modals.confirmCustom.controllers',
    'acme.view.application.membership.detail.modals.cardholderExpiration',
    'acme.view.application.membership.detail.datePicker.controllers',
    'acme.common.factory.notice',
    NoticesComponentModule,
    PermissionsDirectiveModule,
    'acme.common.factory.gridDefinition',
    ButtonGroupActionsServiceModule,
  ])
  .controller('MembershipDetailController', MembershipDetailController).name;
